/**
 * RepConnect - Provider Profile Enhancement System
 * 
 * Manages user-contributed enhancements to provider profiles with privacy controls
 * and data source hierarchy for CRM integration compatibility.
 * 
 * Copyright (c) 2025 RepConnect, LLC
 * 
 * Copyright (c) 2025 RepConnect, LLC
 */

// Privacy levels for profile enhancements
export const PRIVACY_LEVELS = {
  PUBLIC: 'public',           // Visible to all users
  PROFESSIONAL: 'professional', // Visible to verified healthcare professionals
  PRIVATE: 'private',         // Only visible to contributor
  TEAM: 'team'               // Visible to contributor's organization
};

// Data source types with hierarchy (higher number = higher trust)
export const DATA_SOURCES = {
  NPI_REGISTRY: { 
    type: 'npi_registry', 
    priority: 4, 
    label: 'NPI Registry', 
    icon: '🏛️',
    description: 'Official government database'
  },
  PROVIDER_VERIFIED: { 
    type: 'provider_verified', 
    priority: 3, 
    label: 'Provider Verified', 
    icon: '✓',
    description: 'Confirmed by healthcare provider'
  },
  USER_CONTRIBUTED: { 
    type: 'user_contributed', 
    priority: 2, 
    label: 'User Added', 
    icon: '👥',
    description: 'Added by community member'
  },
  MEETING_DERIVED: { 
    type: 'meeting_derived', 
    priority: 1, 
    label: 'Meeting Insights', 
    icon: '📈',
    description: 'Learned from meeting patterns'
  }
};

// Enhanceable fields with their privacy and validation rules
export const ENHANCEMENT_FIELDS = {
  // Contact Information
  officePhone: {
    label: 'Office Phone',
    category: 'contact',
    defaultPrivacy: PRIVACY_LEVELS.PUBLIC,
    allowedPrivacy: [PRIVACY_LEVELS.PUBLIC, PRIVACY_LEVELS.PROFESSIONAL],
    validation: /^\+?[\d\s\-\(\)\.]+$/,
    placeholder: '(555) 123-4567'
  },
  directPhone: {
    label: 'Direct Line',
    category: 'contact',
    defaultPrivacy: PRIVACY_LEVELS.PRIVATE,
    allowedPrivacy: [PRIVACY_LEVELS.PRIVATE, PRIVACY_LEVELS.TEAM],
    validation: /^\+?[\d\s\-\(\)\.]+$/,
    placeholder: '(555) 123-4567 ext 123'
  },
  personalCell: {
    label: 'Personal Cell',
    category: 'contact',
    defaultPrivacy: PRIVACY_LEVELS.PRIVATE,
    allowedPrivacy: [PRIVACY_LEVELS.PRIVATE],
    validation: /^\+?[\d\s\-\(\)\.]+$/,
    placeholder: '(555) 123-4567',
    sensitive: true
  },
  officeEmail: {
    label: 'Office Email',
    category: 'contact',
    defaultPrivacy: PRIVACY_LEVELS.PUBLIC,
    allowedPrivacy: [PRIVACY_LEVELS.PUBLIC, PRIVACY_LEVELS.PROFESSIONAL],
    validation: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    placeholder: 'doctor@practice.com'
  },
  personalEmail: {
    label: 'Personal Email',
    category: 'contact',
    defaultPrivacy: PRIVACY_LEVELS.PRIVATE,
    allowedPrivacy: [PRIVACY_LEVELS.PRIVATE],
    validation: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    placeholder: 'doctor@gmail.com',
    sensitive: true
  },

  // Office Details
  officeHours: {
    label: 'Office Hours',
    category: 'office',
    defaultPrivacy: PRIVACY_LEVELS.PUBLIC,
    allowedPrivacy: [PRIVACY_LEVELS.PUBLIC, PRIVACY_LEVELS.PROFESSIONAL],
    type: 'text',
    placeholder: 'Monday-Friday 8:00 AM - 5:00 PM'
  },
  parkingInstructions: {
    label: 'Parking Instructions',
    category: 'office',
    defaultPrivacy: PRIVACY_LEVELS.PUBLIC,
    allowedPrivacy: [PRIVACY_LEVELS.PUBLIC, PRIVACY_LEVELS.PROFESSIONAL],
    type: 'textarea',
    placeholder: 'Visitor parking on 2nd floor, validate at front desk'
  },
  officeNotes: {
    label: 'Office Access Notes',
    category: 'office',
    defaultPrivacy: PRIVACY_LEVELS.PROFESSIONAL,
    allowedPrivacy: [PRIVACY_LEVELS.PUBLIC, PRIVACY_LEVELS.PROFESSIONAL, PRIVACY_LEVELS.PRIVATE],
    type: 'textarea',
    placeholder: 'Building entrance, security requirements, etc.'
  },

  // Meeting Preferences
  preferredMeetingTime: {
    label: 'Preferred Meeting Time',
    category: 'preferences',
    defaultPrivacy: PRIVACY_LEVELS.PUBLIC,
    allowedPrivacy: [PRIVACY_LEVELS.PUBLIC, PRIVACY_LEVELS.PROFESSIONAL],
    type: 'select',
    options: ['Early lunch (11:00-12:00)', 'Standard lunch (12:00-1:00)', 'Late lunch (1:00-2:00)', 'Coffee meeting', 'Office visit only']
  },
  preferredRestaurants: {
    label: 'Favorite Restaurants',
    category: 'preferences',
    defaultPrivacy: PRIVACY_LEVELS.PUBLIC,
    allowedPrivacy: [PRIVACY_LEVELS.PUBLIC, PRIVACY_LEVELS.PROFESSIONAL],
    type: 'array',
    placeholder: 'Restaurant names they enjoy'
  },
  dietaryRestrictions: {
    label: 'Dietary Restrictions',
    category: 'preferences',
    defaultPrivacy: PRIVACY_LEVELS.PUBLIC,
    allowedPrivacy: [PRIVACY_LEVELS.PUBLIC, PRIVACY_LEVELS.PROFESSIONAL],
    type: 'array',
    options: ['Vegetarian', 'Vegan', 'Gluten-free', 'Kosher', 'Halal', 'No pork', 'No shellfish', 'Lactose intolerant', 'Diabetic-friendly']
  },
  communicationStyle: {
    label: 'Communication Style',
    category: 'preferences',
    defaultPrivacy: PRIVACY_LEVELS.PRIVATE,
    allowedPrivacy: [PRIVACY_LEVELS.PRIVATE, PRIVACY_LEVELS.TEAM],
    type: 'select',
    options: ['Direct and brief', 'Detailed explanations', 'Relationship-focused', 'Data-driven', 'Prefers email', 'Prefers phone calls']
  },

  // Professional Details
  subspecialty: {
    label: 'Subspecialty Focus',
    category: 'professional',
    defaultPrivacy: PRIVACY_LEVELS.PUBLIC,
    allowedPrivacy: [PRIVACY_LEVELS.PUBLIC, PRIVACY_LEVELS.PROFESSIONAL],
    type: 'text',
    placeholder: 'e.g., Interventional Cardiology, Pediatric Oncology'
  },
  researchInterests: {
    label: 'Research Interests',
    category: 'professional',
    defaultPrivacy: PRIVACY_LEVELS.PUBLIC,
    allowedPrivacy: [PRIVACY_LEVELS.PUBLIC, PRIVACY_LEVELS.PROFESSIONAL],
    type: 'array',
    placeholder: 'Clinical research areas of interest'
  },
  administrativeRole: {
    label: 'Administrative Role',
    category: 'professional',
    defaultPrivacy: PRIVACY_LEVELS.PROFESSIONAL,
    allowedPrivacy: [PRIVACY_LEVELS.PUBLIC, PRIVACY_LEVELS.PROFESSIONAL],
    type: 'text',
    placeholder: 'Department Chair, Medical Director, etc.'
  },

  // Relationship Notes (Always Private)
  relationshipNotes: {
    label: 'Relationship Notes',
    category: 'relationship',
    defaultPrivacy: PRIVACY_LEVELS.PRIVATE,
    allowedPrivacy: [PRIVACY_LEVELS.PRIVATE],
    type: 'textarea',
    placeholder: 'Personal notes about your relationship, preferences, important context...',
    sensitive: true
  },
  lastInteraction: {
    label: 'Last Interaction Notes',
    category: 'relationship',
    defaultPrivacy: PRIVACY_LEVELS.PRIVATE,
    allowedPrivacy: [PRIVACY_LEVELS.PRIVATE],
    type: 'textarea',
    placeholder: 'Notes from your last meeting or conversation...',
    sensitive: true
  },
  followUpReminders: {
    label: 'Follow-up Reminders',
    category: 'relationship',
    defaultPrivacy: PRIVACY_LEVELS.PRIVATE,
    allowedPrivacy: [PRIVACY_LEVELS.PRIVATE],
    type: 'textarea',
    placeholder: 'Things to remember for next interaction...',
    sensitive: true
  }
};

// Categories for organizing enhancement fields
export const ENHANCEMENT_CATEGORIES = {
  contact: {
    label: 'Contact Information',
    icon: '📞',
    description: 'Phone numbers, emails, and contact methods'
  },
  office: {
    label: 'Office Details',
    icon: '🏢',
    description: 'Office hours, location details, access information'
  },
  preferences: {
    label: 'Meeting Preferences',
    icon: '⚙️',
    description: 'Meeting times, restaurants, dietary needs'
  },
  professional: {
    label: 'Professional Details',
    icon: '👨‍⚕️',
    description: 'Specialties, research interests, roles'
  },
  relationship: {
    label: 'Relationship Notes',
    icon: '🤝',
    description: 'Private notes and relationship context'
  }
};

// Enhancement data structure
export const createEnhancement = (providerId, fieldName, value, contributorId, privacyLevel = null) => {
  const field = ENHANCEMENT_FIELDS[fieldName];
  if (!field) {
    throw new Error(`Unknown enhancement field: ${fieldName}`);
  }

  const privacy = privacyLevel || field.defaultPrivacy;
  
  if (!field.allowedPrivacy.includes(privacy)) {
    throw new Error(`Privacy level ${privacy} not allowed for field ${fieldName}`);
  }

  return {
    id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
    providerId,
    fieldName,
    fieldLabel: field.label,
    value,
    contributorId,
    privacyLevel: privacy,
    dataSource: DATA_SOURCES.USER_CONTRIBUTED.type,
    category: field.category,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    verified: false,
    verifiedBy: null,
    verifiedAt: null,
    version: 1
  };
};

// CRM export format mapping
export const CRM_EXPORT_FORMATS = {
  SALESFORCE: {
    name: 'Salesforce',
    fields: {
      officePhone: 'Phone',
      officeEmail: 'Email',
      subspecialty: 'Department',
      preferredMeetingTime: 'Meeting_Preference__c',
      dietaryRestrictions: 'Dietary_Restrictions__c'
    }
  },
  HUBSPOT: {
    name: 'HubSpot',
    fields: {
      officePhone: 'phone',
      officeEmail: 'email',
      subspecialty: 'jobtitle',
      preferredMeetingTime: 'meeting_preference',
      relationshipNotes: 'notes_last_contacted'
    }
  },
  PIPEDRIVE: {
    name: 'Pipedrive',
    fields: {
      officePhone: 'phone',
      officeEmail: 'email',
      subspecialty: 'job_title',
      preferredRestaurants: 'custom_restaurant_preferences'
    }
  }
};

export default {
  PRIVACY_LEVELS,
  DATA_SOURCES,
  ENHANCEMENT_FIELDS,
  ENHANCEMENT_CATEGORIES,
  CRM_EXPORT_FORMATS,
  createEnhancement
};